import React, { useState } from "react";
import { Chips } from "primereact/chips";
import { DashboardEditorProps } from "components/ComponentType";
import { DatasetMultiSelect } from "components/basic/dropdowns/dataset/DatasetMultiSelect";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import TimeUnitDropdown from "components/basic/dropdowns/time-unit-dropdown/TimeUnitDropdown";
import { InputSwitch } from "primereact/inputswitch";
import { SankeyDiagramNode, SankeyDiagramProps } from "../sankey-types";
import NodesEditor from "./NodesEditor";
import LinksEditor from "./LinksEditor";
import { colorsSets } from "../colorsSets";

interface NodesChangeEvent {
  originalEvent: React.SyntheticEvent;
  preventDefault: () => void;
  stopPropagation: () => void;
  target: {
    name: string | null;
    id: string | null;
    value: string[] | SankeyDiagramNode[];
  };
  value: string[] | SankeyDiagramNode[];
}

const SankeyEditor = ({ config, onConfigChange }: DashboardEditorProps<SankeyDiagramProps>) => {
  const [editLinksEditorDialogVisible, setLinksEditorDialogVisibility] = useState<boolean>(false);
  const [editNodesEditorDialogVisible, setNodesEditorDialogVisibility] = useState<boolean>(false);

  const handleEditLinksClick = () => {
    setLinksEditorDialogVisibility(true);
  };

  const handleEditNodesClick = () => {
    setNodesEditorDialogVisibility(true);
  };

  const handleNodesChange = (e: NodesChangeEvent) => {
    let newNodes: SankeyDiagramNode[] = [];

    if (Array.isArray(e.value) && typeof e.value[0] === "string") {
      newNodes = (e.value as string[]).map((id: string) => ({ id, title: config.graph.nodes.find(node => node.id === id)?.title || "" }));
    } else if (Array.isArray(e.value) && typeof e.value[0] === "object") {
      newNodes = e.value as SankeyDiagramNode[];
    }

    const newNodeIds = newNodes.map((node: SankeyDiagramNode) => node.id);
    const oldNodeIds = config.graph.nodes.map(node => node.id);

    const addedNodes = newNodeIds.filter((id: string) => !oldNodeIds.includes(id));
    const removedNodes = oldNodeIds.filter(id => !newNodeIds.includes(id));

    const newXPositions = [...(config.nodesPosition?.x ?? [])];
    const newYPositions = [...(config.nodesPosition?.y ?? [])];

    addedNodes.forEach(() => {
      newXPositions.push(0.0);
      newYPositions.push(0.0);
    });

    removedNodes.forEach(id => {
      const index = oldNodeIds.indexOf(id);
      if (index > -1) {
        newXPositions.splice(index, 1);
        newYPositions.splice(index, 1);
      }
    });

    onConfigChange({
      ...config,
      graph: { ...config.graph, nodes: newNodes },
      nodesPosition: { x: newXPositions, y: newYPositions },
    });
  };

  return (
    <div className="flex flex-column gap-2">
      <div className="flex flex-column gap-2">
        <label htmlFor="nodesIds">Nodes IDs</label>
        <div className="flex flex-row">
          <Chips
            value={config.graph.nodes ? config.graph.nodes.map(e => e.id) : []}
            onChange={handleNodesChange}
            style={{ width: "100%" }}
          />
          {config.graph.nodes.length > 0 && (
            <>
              <Button
                icon="pi pi-pencil"
                type="button"
                className="p-button-secondary"
                tooltip="Edit Lines"
                onClick={handleEditNodesClick}
              />
              <Dialog
                onHide={() => setNodesEditorDialogVisibility(false)}
                visible={editNodesEditorDialogVisible}
                header="Edit Line style"
                style={{ width: "min(100vw, 1000px)", height: "400px" }}
              >
                <NodesEditor config={config} onConfigChange={onConfigChange} />
              </Dialog>
            </>
          )}
        </div>
        <div className="flex flex-row gap-2 justify-content-between mb-2">
          <div className="col-2 gap-2 flex flex-column justify-content-center align-items-start">
            <label htmlFor="nodesGap">Nodes Gap</label>
            <InputNumber
              id="nodesGap"
              value={config.nodesGap}
              onValueChange={e => onConfigChange({ ...config, nodesGap: e.value || undefined })}
            />
          </div>
          <div className="col-2 gap-2 flex flex-column justify-content-center align-items-start">
            <label htmlFor="colorSchema">Color Schema</label>
            <Dropdown
              id="colorSchema"
              value={config.colorScheme}
              options={colorsSets}
              onChange={e => onConfigChange({ ...config, colorScheme: e.value })}
              placeholder="Select color palette"
            />
          </div>
          <div className="flex flex-column gap-2 w-1">
            <label htmlFor="overridePositions">Override Positions</label>
            <InputSwitch
              id="overridePositions"
              checked={config.overridePositions}
              onChange={e => onConfigChange({ ...config, overridePositions: e.value })}
            />
          </div>
        </div>
      </div>
      {config.graph.nodes.length > 1 && (
        <div className="flex flex-column gap-2">
          <label htmlFor="datasets">Links Datasets</label>
          <div className="flex gap-2">
            <DatasetMultiSelect
              value={config.graph.links.map(e => e.identifier)}
              onChange={e =>
                onConfigChange({
                  ...config,
                  graph: {
                    ...config.graph,
                    links: e.map(identifier => ({
                      source: "source",
                      target: "target",
                      identifier,
                      customUnit: undefined,
                      customDivider: undefined,
                    })),
                  },
                })
              }
            />
            {config.graph.links.length > 0 && (
              <>
                <Button
                  icon="pi pi-pencil"
                  type="button"
                  className="p-button-secondary"
                  tooltip="Edit Lines"
                  onClick={handleEditLinksClick}
                />
                <Dialog
                  onHide={() => setLinksEditorDialogVisibility(false)}
                  visible={editLinksEditorDialogVisible}
                  header="Edit Line style"
                  style={{ width: "min(100vw, 1000px)", height: "400px" }}
                >
                  <LinksEditor config={config} onConfigChange={onConfigChange} />
                </Dialog>
              </>
            )}
          </div>
        </div>
      )}
      <div className="flex flex-row justify-content-start">
        <div className="col-2 gap-2 flex flex-column justify-content-start align-items-start">
          <label htmlFor="decimalPlaces">Decimal places</label>
          <div className="p-inputgroup w-full">
            <InputNumber
              id="decimalPlaces"
              value={config.decimalPlaces as number}
              onValueChange={e => onConfigChange({ ...config, decimalPlaces: e.value as number })}
            />
          </div>
        </div>
        <div className="col-3 gap-2 flex flex-column justify-content-center align-items-center">
          <label htmlFor="percentageDecimalPlaces">Percentage Decimal places</label>
          <div className="p-inputgroup w-full">
            <InputNumber
              id="percentageDecimalPlaces"
              value={config.percentageDecimalPlaces as number}
              onValueChange={e =>
                onConfigChange({ ...config, percentageDecimalPlaces: e.value as number })
              }
            />
          </div>
        </div>
      </div>

      <div className="flex flex-row justify-content-start">
        <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
          <label htmlFor="height">Height</label>
          <div className="p-inputgroup w-full">
            <InputNumber
              value={config.height as number}
              onChange={e => onConfigChange({ ...config, height: e.value as number })}
            />
            <span className="p-inputgroup-addon">px</span>
          </div>
        </div>
        <div className="col-4 gap-2 flex flex-column justify-content-center align-items-center">
          <label htmlFor="timeOffset">Time Offset</label>
          <div className="p-inputgroup">
            <InputNumber
              value={config.timeOffset as number}
              onChange={e => onConfigChange({ ...config, timeOffset: e.value as number })}
            />
            <TimeUnitDropdown
              id="timeOffsetUnits"
              className="p-inputgroup-addon p-0"
              value={config?.timeOffsetUnits ?? ""}
              onChange={e =>
                onConfigChange({ ...config, timeOffsetUnits: e.value.toString().toUpperCase() })
              }
            />
          </div>
        </div>
        <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
          <label htmlFor="alignStartTo">Align Start To</label>
          <TimeUnitDropdown
            id="alignStartTo"
            className="p-0"
            value={config?.alignStartTo ? config.alignStartTo : ""}
            onChange={e =>
              onConfigChange({ ...config, alignStartTo: e.value.toString().toUpperCase() })
            }
          />
        </div>
      </div>
    </div>
  );
};

export default SankeyEditor;
